import { MathUtils, Vector3, type PerspectiveCamera } from "three/webgpu";
import { opticalLensingConfig } from "../config/opticalLensingConfig";

export type OpticalLensingProfile = "point" | "orbital" | "connector";

type PreparedFrame = {
  camera: PerspectiveCamera;
  viewportWidth: number;
  viewportHeight: number;
  singularityX: number;
  singularityY: number;
  singularityZ: number;
  cameraX: number;
  cameraY: number;
  cameraZ: number;
  quaternionX: number;
  quaternionY: number;
  quaternionZ: number;
  quaternionW: number;
  fov: number;
  aspect: number;
};

export class OpticalLensingProjector {
  private readonly sourceNdc = new Vector3();
  private readonly singularityNdc = new Vector3();
  private readonly cameraPosition = new Vector3();
  private prepared: PreparedFrame | null = null;
  private viewportWidthSafe = 1;
  private viewportHeightSafe = 1;
  private influenceRadiusPx = 0;

  prepareFrame(
    camera: PerspectiveCamera,
    viewportWidth: number,
    viewportHeight: number,
    singularityPosition: Vector3,
  ) {
    this.prepare(camera, viewportWidth, viewportHeight, singularityPosition);
  }

  project(
    camera: PerspectiveCamera,
    viewportWidth: number,
    viewportHeight: number,
    worldPosition: Vector3,
    singularityPosition: Vector3,
    target: Vector3,
    profile: OpticalLensingProfile = "point",
  ) {
    this.ensurePrepared(camera, viewportWidth, viewportHeight, singularityPosition);
    return this.projectPrepared(worldPosition, singularityPosition, target, profile);
  }

  projectBlended(
    camera: PerspectiveCamera,
    viewportWidth: number,
    viewportHeight: number,
    worldPosition: Vector3,
    singularityPosition: Vector3,
    target: Vector3,
    fromProfile: OpticalLensingProfile,
    toProfile: OpticalLensingProfile,
    blend: number,
  ) {
    this.ensurePrepared(camera, viewportWidth, viewportHeight, singularityPosition);
    return this.projectBlendedPrepared(worldPosition, singularityPosition, target, fromProfile, toProfile, blend);
  }

  projectPrepared(
    worldPosition: Vector3,
    singularityPosition: Vector3,
    target: Vector3,
    profile: OpticalLensingProfile = "point",
  ) {
    return this.projectWithMaxShiftPrepared(
      worldPosition,
      singularityPosition,
      target,
      this.maxShiftForProfile(profile),
    );
  }

  projectBlendedPrepared(
    worldPosition: Vector3,
    singularityPosition: Vector3,
    target: Vector3,
    fromProfile: OpticalLensingProfile,
    toProfile: OpticalLensingProfile,
    blend: number,
  ) {
    const maxShiftPx = MathUtils.lerp(
      this.maxShiftForProfile(fromProfile),
      this.maxShiftForProfile(toProfile),
      MathUtils.clamp(blend, 0, 1),
    );
    return this.projectWithMaxShiftPrepared(worldPosition, singularityPosition, target, maxShiftPx);
  }

  private projectWithMaxShiftPrepared(
    worldPosition: Vector3,
    singularityPosition: Vector3,
    target: Vector3,
    maxShiftPx: number,
  ) {
    if (worldPosition.distanceToSquared(singularityPosition) < 0.000001) return target.copy(worldPosition);

    const camera = this.prepared?.camera;
    if (!camera) return target.copy(worldPosition);
    this.sourceNdc.copy(worldPosition).project(camera);
    if (this.sourceNdc.z < -1 || this.sourceNdc.z > 1) return target.copy(worldPosition);

    const dxPx = (this.sourceNdc.x - this.singularityNdc.x) * this.viewportWidthSafe * 0.5;
    const dyPx = (this.sourceNdc.y - this.singularityNdc.y) * this.viewportHeightSafe * 0.5;
    const radiusSquaredPx = dxPx * dxPx + dyPx * dyPx;
    const influenceRadiusSquaredPx = this.influenceRadiusPx * this.influenceRadiusPx;
    if (radiusSquaredPx <= 0.00000001 || radiusSquaredPx >= influenceRadiusSquaredPx) return target.copy(worldPosition);

    const radiusPx = Math.sqrt(radiusSquaredPx);
    const innerRadiusPx = this.influenceRadiusPx * opticalLensingConfig.innerRadiusRatio;
    const normalized = MathUtils.clamp(
      (this.influenceRadiusPx - Math.max(radiusPx, innerRadiusPx)) /
        Math.max(this.influenceRadiusPx - innerRadiusPx, 0.001),
      0,
      1,
    );
    const smooth = normalized * normalized * (3 - 2 * normalized);
    const shiftPx = Math.pow(smooth, opticalLensingConfig.falloffExponent) * maxShiftPx;
    const invRadius = 1 / radiusPx;

    this.sourceNdc.x += (dxPx * invRadius * shiftPx * 2) / this.viewportWidthSafe;
    this.sourceNdc.y += (dyPx * invRadius * shiftPx * 2) / this.viewportHeightSafe;
    return target.copy(this.sourceNdc).unproject(camera);
  }

  private ensurePrepared(
    camera: PerspectiveCamera,
    viewportWidth: number,
    viewportHeight: number,
    singularityPosition: Vector3,
  ) {
    const frame = this.prepared;
    if (
      frame?.camera === camera &&
      frame.viewportWidth === viewportWidth &&
      frame.viewportHeight === viewportHeight &&
      frame.singularityX === singularityPosition.x &&
      frame.singularityY === singularityPosition.y &&
      frame.singularityZ === singularityPosition.z &&
      frame.cameraX === camera.position.x &&
      frame.cameraY === camera.position.y &&
      frame.cameraZ === camera.position.z &&
      frame.quaternionX === camera.quaternion.x &&
      frame.quaternionY === camera.quaternion.y &&
      frame.quaternionZ === camera.quaternion.z &&
      frame.quaternionW === camera.quaternion.w &&
      frame.fov === camera.fov &&
      frame.aspect === camera.aspect
    ) {
      return;
    }
    this.prepare(camera, viewportWidth, viewportHeight, singularityPosition);
  }

  private prepare(
    camera: PerspectiveCamera,
    viewportWidth: number,
    viewportHeight: number,
    singularityPosition: Vector3,
  ) {
    this.viewportWidthSafe = Math.max(viewportWidth, 1);
    this.viewportHeightSafe = Math.max(viewportHeight, 1);
    this.singularityNdc.copy(singularityPosition).project(camera);
    camera.getWorldPosition(this.cameraPosition);
    const singularityDistance = Math.max(this.cameraPosition.distanceTo(singularityPosition), 0.001);
    const worldPerPixel =
      (2 * Math.tan(MathUtils.degToRad(camera.fov) * 0.5) * singularityDistance) / this.viewportHeightSafe;
    this.influenceRadiusPx = opticalLensingConfig.influenceRadiusWorld / Math.max(worldPerPixel, 0.0001);
    const frame = this.prepared ?? {
      camera,
      viewportWidth,
      viewportHeight,
      singularityX: 0,
      singularityY: 0,
      singularityZ: 0,
      cameraX: 0,
      cameraY: 0,
      cameraZ: 0,
      quaternionX: 0,
      quaternionY: 0,
      quaternionZ: 0,
      quaternionW: 1,
      fov: 0,
      aspect: 0,
    };
    frame.camera = camera;
    frame.viewportWidth = viewportWidth;
    frame.viewportHeight = viewportHeight;
    frame.singularityX = singularityPosition.x;
    frame.singularityY = singularityPosition.y;
    frame.singularityZ = singularityPosition.z;
    frame.cameraX = camera.position.x;
    frame.cameraY = camera.position.y;
    frame.cameraZ = camera.position.z;
    frame.quaternionX = camera.quaternion.x;
    frame.quaternionY = camera.quaternion.y;
    frame.quaternionZ = camera.quaternion.z;
    frame.quaternionW = camera.quaternion.w;
    frame.fov = camera.fov;
    frame.aspect = camera.aspect;
    this.prepared = frame;
  }

  private maxShiftForProfile(profile: OpticalLensingProfile) {
    if (profile === "orbital") return opticalLensingConfig.orbitalMaxShiftPx;
    if (profile === "connector") return opticalLensingConfig.connectorMaxShiftPx;
    return opticalLensingConfig.pointMaxShiftPx;
  }
}
